import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, API, getToken } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { useProjects } from "../context/ProjectContext";
import { useCan, ROLE_LABEL, ROLE_BADGE_CLASS } from "../lib/permissions";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Plus, DownloadSimple, UploadSimple, SignOut, CaretDown, User as UserIcon,
  Users as UsersIcon, ClockClockwise, Trash as TrashIcon, Gear, FolderOpen, Buildings, ChartLineUp, ChartPie, Kanban,
} from "@phosphor-icons/react";
import KpiCards from "../components/tracker/KpiCards";
import FilterBar from "../components/tracker/FilterBar";
import TaskTable from "../components/tracker/TaskTable";
import TaskForm from "../components/tracker/TaskForm";
import Pagination from "../components/tracker/Pagination";
import BulkActionBar from "../components/tracker/BulkActionBar";
import TodayCard from "../components/tracker/TodayCard";
import { GlobalSearchTrigger } from "../components/tracker/GlobalSearch";
import FilterPresets from "../components/tracker/FilterPresets";
import NotificationBell from "../components/NotificationBell";
import { BrandMark, BRAND } from "../components/Brand";
import { TASK_COLUMNS } from "../lib/constants";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const SIZES_ALLOWED = [25, 50, 100, 200];

const DEFAULT_VISIBLE = [
  "module", "object_id", "title", "priority", "overall_status", "completion_pct",
  "development_status", "risk_level", "delay_days", "functional_owner",
  "technical_resource", "target_date", "updated_by", "updated_at",
];

export default function Dashboard() {
  const { user, logout } = useAuth();
  const can = useCan();
  const { projects, activeProjects, projectId, setProjectId, project, customer, loading: projLoading } = useProjects();
  const [searchParams] = useSearchParams();

  // Deep link support: ?project_id=... selects that project (even if inactive)
  // ?search=... seeds the search filter, ?focus=<task_id> highlights the row.
  const [focusTaskId, setFocusTaskId] = useState(null);
  useEffect(() => {
    const qp = searchParams.get("project_id");
    if (qp && qp !== projectId && projects.some((p) => p.id === qp)) {
      setProjectId(qp);
    }
    const search = searchParams.get("search");
    if (search) {
      setFilters((f) => (f.search === search ? f : { ...f, search }));
    }
    const focus = searchParams.get("focus");
    if (focus) setFocusTaskId(focus);
  }, [searchParams, projects]); // eslint-disable-line
  const [filters, setFilters] = useState({});
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => {
    const saved = Number(localStorage.getItem("ptt_page_size"));
    return SIZES_ALLOWED.includes(saved) ? saved : 50;
  });
  const [totalPages, setTotalPages] = useState(1);
  const [totalTasks, setTotalTasks] = useState(0);
  const [sort, setSort] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem("ptt_sort") || "null");
      if (s && s.by) return s;
    } catch { /* ignore */ }
    return { by: "created_at", dir: "desc" };
  });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem("ptt_visible_cols");
    if (saved) {
      try { return JSON.parse(saved); } catch { /* ignore */ }
    }
    return DEFAULT_VISIBLE;
  });
  const fileRef = useRef(null);

  useEffect(() => {
    localStorage.setItem("ptt_visible_cols", JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  useEffect(() => {
    localStorage.setItem("ptt_page_size", String(pageSize));
  }, [pageSize]);

  useEffect(() => {
    localStorage.setItem("ptt_sort", JSON.stringify(sort));
  }, [sort]);

  // Reset to page 1 whenever filters, sort or page size change
  useEffect(() => { setPage(1); }, [filters, pageSize, sort]);

  // Clear selection when filters change (selection may reference non-matching rows)
  useEffect(() => { setSelectedIds(new Set()); }, [filters]);

  const handleSort = (columnKey) => {
    setSort((cur) => {
      if (cur.by === columnKey) {
        return { by: columnKey, dir: cur.dir === "asc" ? "desc" : "asc" };
      }
      return { by: columnKey, dir: "asc" };
    });
  };

  const queryParams = useMemo(() => {
    const p = { project_id: projectId };
    Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [filters, projectId]);

  const loadTasks = useCallback(async () => {
    if (!projectId) { setTasks([]); setStats({}); setTotalTasks(0); setTotalPages(1); setLoading(false); return; }
    setLoading(true);
    try {
      const [{ data: tRes }, { data: sRes }] = await Promise.all([
        api.get("/tasks", { params: { ...queryParams, page, page_size: pageSize, sort_by: sort.by, sort_dir: sort.dir } }),
        api.get("/tasks/stats/summary", { params: { project_id: projectId } }),
      ]);
      setTasks(tRes.tasks || []);
      setTotalPages(tRes.total_pages || 1);
      setTotalTasks(tRes.total || 0);
      if (tRes.page && tRes.page !== page) setPage(tRes.page);
      setStats(sRes || {});
    } catch (e) {
      toast.error("Failed to load tasks");
    } finally {
      setLoading(false);
    }
  }, [queryParams, page, pageSize, sort, projectId]);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  const handleCreateOrUpdate = async (payload) => {
    try {
      if (editing) {
        await api.put(`/tasks/${editing.id}`, payload);
        toast.success("Task updated");
      } else {
        await api.post("/tasks", payload, { params: { project_id: projectId } });
        toast.success("Task created");
      }
      setEditing(null);
      await loadTasks();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
      throw e;
    }
  };

  const handleInlineUpdate = async (id, patch) => {
    try {
      await api.patch(`/tasks/${id}`, patch);
      toast.success("Updated");
      loadTasks();
    } catch {
      toast.error("Update failed");
    }
  };

  const toggleRow = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAllOnPage = (ids, select) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (select) ids.forEach((i) => next.add(i));
      else ids.forEach((i) => next.delete(i));
      return next;
    });
  };

  const selectAllAcrossPages = async () => {
    try {
      const { data } = await api.get("/tasks/bulk/ids", { params: queryParams });
      setSelectedIds(new Set(data.ids || []));
      toast.success(`Selected ${data.total} matching tasks`);
    } catch {
      toast.error("Failed to select all");
    }
  };
  const handleBulkUpdate = async (patch) => {
    try {
      const ids = Array.from(selectedIds);
      const { data } = await api.post("/tasks/bulk/update", { ids, patch });
      toast.success(`Updated ${data.updated} task${data.updated === 1 ? "" : "s"}`);
      setSelectedIds(new Set());
      loadTasks();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk update failed");
    }
  };

  const handleBulkDelete = async () => {
    try {
      const ids = Array.from(selectedIds);
      const { data } = await api.post("/tasks/bulk/delete", { ids });
      const deletedIds = data.ids || [];
      const deletedCount = data.deleted || 0;
      setSelectedIds(new Set());
      loadTasks();

      if (deletedCount > 0) {
        toast.success(`Deleted ${deletedCount} task${deletedCount === 1 ? "" : "s"}`, {
          duration: 5000,
          action: {
            label: "Undo",
            onClick: async () => {
              try {
                const { data: r } = await api.post("/tasks/bulk/restore", { ids: deletedIds });
                toast.success(`Restored ${r.restored || 0} task${(r.restored || 0) === 1 ? "" : "s"}`);
                loadTasks();
              } catch (err) {
                toast.error(err.response?.data?.detail || "Restore failed");
              }
            },
          },
        });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk delete failed");
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    const victim = deleting;
    try {
      await api.delete(`/tasks/${victim.id}`);
      setDeleting(null);
      loadTasks();
      toast.success("Task deleted", {
        duration: 5000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.post("/tasks/bulk/restore", { ids: [victim.id] });
              toast.success("Task restored");
              loadTasks();
            } catch (err) {
              toast.error(err.response?.data?.detail || "Restore failed");
            }
          },
        },
      });
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/tasks/import", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.inserted} tasks from ${data.format === "csv" ? "CSV" : "Excel"}`);
      loadTasks();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const doExport = async (scope) => {
    const params = new URLSearchParams({ ...queryParams, scope });
    const url = `${API}/tasks/export?${params.toString()}`;
    try {
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const dispo = res.headers.get("Content-Disposition") || "";
      const match = dispo.match(/filename="?([^";]+)"?/);
      const filename = match ? match[1] : `task_tracker.xlsx`;
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success(`Exported ${scope === "filtered" ? "filtered" : "all"} tasks`);
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col" data-testid="dashboard-page">
      {/* Header */}
      <header className="h-16 border-b border-[#E5E5E5] bg-white flex items-center px-6 justify-between shrink-0">
        <div className="flex items-center gap-3">
          <BrandMark />
          <div className="flex flex-col leading-tight">
            <span
              className="text-sm font-black tracking-tight"
              style={{ fontFamily: "Chivo, sans-serif", color: BRAND.colors.ink }}
            >
              PROJECT TRACKER
            </span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
              {customer ? `${customer.name} · ${project?.name}` : "No project selected"}
            </span>
          </div>
        </div>

        {/* Project selector (Active only) + Global search */}
        <div className="hidden md:flex items-center gap-3 flex-1 justify-center max-w-2xl">
          {activeProjects.length > 0 ? (
            <div className="flex items-center gap-2">
              <FolderOpen size={14} weight="bold" className="text-neutral-500" />
              <Select value={projectId || ""} onValueChange={setProjectId}>
                <SelectTrigger data-testid="project-selector" className="h-9 min-w-[200px]">
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  {activeProjects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
          <GlobalSearchTrigger />
        </div>

        <div className="flex items-center gap-2">
          <NotificationBell />
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={handleImport}
            data-testid="import-file-input"
          />
          {can.admin ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileRef.current?.click()}
              data-testid="import-excel-btn"
              className="h-9"
            >
              <UploadSimple size={16} weight="bold" className="mr-2" />
              Import (.xlsx / .csv)
            </Button>
          ) : null}

          <Link to="/analytics">
            <Button
              variant="outline"
              size="sm"
              data-testid="analytics-link-btn"
              className="h-9 border-transparent text-white hover:opacity-90"
              style={{
                background: `linear-gradient(135deg, ${BRAND.colors.blue}, ${BRAND.colors.green})`,
              }}
            >
              <ChartPie size={16} weight="bold" className="mr-2" />
              Analytics
            </Button>
          </Link>

          <Link to="/plan">
            <Button
              variant="outline"
              size="sm"
              data-testid="plan-link-btn"
              className="h-9 border-transparent text-white hover:opacity-90"
              style={{
                background: `linear-gradient(135deg, ${BRAND.colors.orange}, #EF4444)`,
              }}
            >
              <Kanban size={16} weight="bold" className="mr-2" />
              Plan
            </Button>
          </Link>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="export-menu-btn" className="h-9">
                <DownloadSimple size={16} weight="bold" className="mr-2" />
                Export
                <CaretDown size={12} weight="bold" className="ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => doExport("filtered")} data-testid="export-filtered-btn">
                Export filtered view
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => doExport("all")} data-testid="export-all-btn">
                Export all rows
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {can.edit ? (
            <Button
              size="sm"
              onClick={() => { setEditing(null); setFormOpen(true); }}
              data-testid="new-task-btn"
              className="h-9 text-white"
              style={{ backgroundColor: BRAND.colors.blue }}
              onMouseOver={(e) => (e.currentTarget.style.backgroundColor = BRAND.colors.blueDeep)}
              onMouseOut={(e) => (e.currentTarget.style.backgroundColor = BRAND.colors.blue)}
            >
              <Plus size={16} weight="bold" className="mr-2" />
              New Task
            </Button>
          ) : null}

          <div className="mx-2 h-6 w-px bg-neutral-200" />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="user-menu-btn"
                className="h-9 px-3 flex items-center gap-2 rounded-md hover:bg-neutral-100"
              >
                <div className="h-6 w-6 rounded-full bg-neutral-200 flex items-center justify-center">
                  <UserIcon size={12} weight="bold" />
                </div>
                <span className="text-xs font-medium hidden sm:inline">{user?.email}</span>
                <span
                  data-testid="user-role-badge"
                  className={`hidden sm:inline text-[9px] font-bold uppercase tracking-wider border rounded px-1.5 py-0.5 ${ROLE_BADGE_CLASS[user?.role] || ""}`}
                >
                  {ROLE_LABEL[user?.role] || user?.role}
                </span>
                <CaretDown size={12} weight="bold" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5 text-xs text-neutral-500">
                Signed in as
                <div className="font-medium text-neutral-900 mt-0.5">{user?.email}</div>
                <div className="mt-1 text-[10px] uppercase tracking-wider">{ROLE_LABEL[user?.role]}</div>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild data-testid="portfolio-link">
                <Link to="/portfolio" className="cursor-pointer">
                  <ChartLineUp size={14} weight="bold" className="mr-2" />
                  Portfolio Overview
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild data-testid="analytics-menu-link">
                <Link to="/analytics" className="cursor-pointer">
                  <ChartPie size={14} weight="bold" className="mr-2" />
                  Analytics
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild data-testid="plan-menu-link">
                <Link to="/plan" className="cursor-pointer">
                  <Kanban size={14} weight="bold" className="mr-2" />
                  Project Plan (Gantt)
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild data-testid="activity-log-link">
                <Link to="/activity" className="cursor-pointer">
                  <ClockClockwise size={14} weight="bold" className="mr-2" />
                  Activity Log
                </Link>
              </DropdownMenuItem>
              {can.admin ? (
                <DropdownMenuItem asChild data-testid="setup-link">
                  <Link to="/setup" className="cursor-pointer">
                    <Gear size={14} weight="bold" className="mr-2" />
                    Setup (Customers &amp; Projects)
                  </Link>
                </DropdownMenuItem>
              ) : null}
              {can.admin ? (
                <DropdownMenuItem asChild data-testid="trash-link">
                  <Link to="/trash" className="cursor-pointer">
                    <TrashIcon size={14} weight="bold" className="mr-2" />
                    Trash
                  </Link>
                </DropdownMenuItem>
              ) : null}
              {can.admin ? (
                <DropdownMenuItem asChild data-testid="manage-users-link">
                  <Link to="/users" className="cursor-pointer">
                    <UsersIcon size={14} weight="bold" className="mr-2" />
                    Manage Users
                  </Link>
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem onClick={logout} data-testid="logout-btn">
                <SignOut size={14} weight="bold" className="mr-2" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Body */}
      <div className="flex-1 flex flex-col p-6 gap-4 overflow-hidden">
        {!projLoading && activeProjects.length === 0 && (!project || project.status !== "Inactive") ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="max-w-md text-center bg-white border border-[#E5E5E5] rounded-lg p-10">
              <Buildings size={40} weight="thin" className="text-neutral-400 mx-auto mb-3" />
              <h2 className="text-xl font-black tracking-tight mb-2" style={{ fontFamily: "Chivo, sans-serif" }}>
                {projects.length === 0 ? "No projects yet" : "No active projects"}
              </h2>
              <p className="text-sm text-neutral-500 mb-4">
                {can.admin
                  ? projects.length === 0
                    ? "Head to Setup to create your first customer and project."
                    : "All your projects are archived. Reactivate one in Setup to continue."
                  : "You have not been granted access to any active project. Please ask an administrator."}
              </p>
              {can.admin ? (
                <Link to="/setup">
                  <Button
                    data-testid="empty-goto-setup-btn"
                    className="text-white"
                    style={{ backgroundColor: BRAND.colors.blue }}
                    onMouseOver={(e) => (e.currentTarget.style.backgroundColor = BRAND.colors.blueDeep)}
                    onMouseOut={(e) => (e.currentTarget.style.backgroundColor = BRAND.colors.blue)}
                  >
                    <Gear size={14} weight="bold" className="mr-2" /> Go to Setup
                  </Button>
                </Link>
              ) : null}
            </div>
          </div>
        ) : (
        <>
        {project && project.status === "Inactive" ? (
          <div
            data-testid="archived-banner"
            className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5"
          >
            <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border bg-neutral-100 text-neutral-600 border-neutral-200">
              Archived
            </span>
            <p className="text-sm text-amber-900 flex-1">
              This project is <span className="font-semibold">Inactive</span> and hidden from the project selector. Data is preserved and read-only visible here.
            </p>
            {can.admin ? (
              <Link to="/setup">
                <Button variant="outline" size="sm" className="h-8">
                  <Gear size={12} weight="bold" className="mr-1.5" /> Reactivate
                </Button>
              </Link>
            ) : null}
          </div>
        ) : null}
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p
              className="text-[10px] uppercase tracking-[0.3em] text-neutral-500"
              style={{ fontFamily: "IBM Plex Mono, monospace" }}
            >
              Live tracker
            </p>
            <h1
              className="text-3xl font-black tracking-tight mt-1"
              style={{ fontFamily: "Chivo, sans-serif" }}
            >
              Task Overview
            </h1>
          </div>
          <div className="text-xs text-neutral-500">
            <span className="font-mono tabular-nums text-neutral-900" data-testid="task-count-value">
              {tasks.length}
            </span>{" "}
            of{" "}
            <span className="font-mono tabular-nums">{totalTasks}</span> tasks
            {loading ? " · loading..." : ""}
          </div>
        </div>

        <KpiCards stats={stats} />

        <TodayCard refreshKey={totalTasks} />

        <FilterPresets
          projectId={projectId}
          filters={filters}
          sort={sort}
          onApply={({ filters: f, sort: s }) => {
            setFilters(f || {});
            if (s && s.by) setSort(s);
          }}
        />

        <FilterBar
          filters={filters}
          onChange={setFilters}
          onClear={() => setFilters({})}
          visibleColumns={visibleColumns}
          setVisibleColumns={setVisibleColumns}
        />

        {selectedIds.size > 0 ? (
          <BulkActionBar
            selectedCount={selectedIds.size}
            totalMatching={totalTasks}
            allSelected={selectedIds.size >= totalTasks}
            onSelectAllAcrossPages={selectAllAcrossPages}
            onClear={() => setSelectedIds(new Set())}
            canEdit={can.edit}
            canDelete={can.admin}
            onBulkUpdate={handleBulkUpdate}
            onBulkDelete={handleBulkDelete}
          />
        ) : null}

        <TaskTable
          tasks={tasks}
          visibleColumns={visibleColumns}
          sort={sort}
          onSort={handleSort}
          canEdit={can.edit}
          canDelete={can.admin}
          onEdit={(t) => { setEditing(t); setFormOpen(true); }}
          onDelete={(t) => setDeleting(t)}
          onInlineUpdate={handleInlineUpdate}
          selectedIds={can.edit ? selectedIds : null}
          onToggleRow={can.edit ? toggleRow : null}
          onToggleAllOnPage={can.edit ? toggleAllOnPage : null}
          focusTaskId={focusTaskId}
          onFocusHandled={() => setFocusTaskId(null)}
        />

        <Pagination
          page={page}
          pageSize={pageSize}
          total={totalTasks}
          totalPages={totalPages}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
        </>
        )}
      </div>

      <TaskForm
        open={formOpen}
        onOpenChange={(v) => { setFormOpen(v); if (!v) setEditing(null); }}
        initial={editing}
        onSubmit={handleCreateOrUpdate}
      />

      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this task?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove <span className="font-semibold">{deleting?.object_id || deleting?.title}</span> from the tracker.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              data-testid="delete-confirm-btn"
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
